import { Check, X } from "lucide-react";
import { cn } from "../cn";

export const SPEND_STATES = [
  "Proposed",
  "Approved for spend",
  "Committed",
  "Evidence submitted",
  "Confirmed",
] as const;

export type SpendRibbonState = (typeof SPEND_STATES)[number];

/** 5-state spend ribbon for ask detail (spec 14.4). */
export function StatusRibbon({
  current,
  terminal,
}: {
  current: SpendRibbonState;
  /** Optional terminal block overriding the trailing steps. */
  terminal?: { label: "Rejected" | "Flagged"; atIndex: number };
}) {
  const currentIndex = SPEND_STATES.indexOf(current);
  return (
    <div className="flex items-stretch gap-1" role="list" aria-label="Spend status">
      {SPEND_STATES.map((state, i) => {
        if (terminal && i === terminal.atIndex) {
          return (
            <Step key={state} label={terminal.label} state="terminal" />
          );
        }
        if (terminal && i > terminal.atIndex) return null;
        const stepState = i < currentIndex ? "complete" : i === currentIndex ? "current" : "upcoming";
        return <Step key={state} label={state} state={stepState} index={i + 1} />;
      })}
    </div>
  );
}

function Step({
  label,
  state,
  index,
}: {
  label: string;
  state: "complete" | "current" | "upcoming" | "terminal";
  index?: number;
}) {
  return (
    <div
      role="listitem"
      className={cn(
        "flex min-w-[56px] flex-1 flex-col items-center gap-1 rounded-md border px-2 py-2 text-center",
        state === "complete" && "border-transparent bg-okSoft text-ok",
        state === "current" && "border-brand bg-brandSoft text-brand",
        state === "upcoming" && "border-transparent bg-subtle text-faint",
        state === "terminal" && "border-transparent bg-badSoft text-bad",
      )}
    >
      <span
        className={cn(
          "flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold",
          state === "complete" && "bg-ok text-white",
          state === "current" && "bg-brand text-white",
          state === "upcoming" && "bg-card text-faint",
          state === "terminal" && "bg-bad text-white",
        )}
      >
        {state === "complete" ? (
          <Check className="h-3 w-3" />
        ) : state === "terminal" ? (
          <X className="h-3 w-3" />
        ) : (
          index
        )}
      </span>
      <span className="text-[11px] font-medium leading-tight">{label}</span>
    </div>
  );
}
